/**
 * VDM "special instructions 2" (SI2) batch-number formatting.
 *
 * A VDM-first EDI batch carries its batch numbers in the VDM's SI2 field so the
 * AS400 side can tie the debit memo back to the 844D batches it covers. Two
 * callers need the identical string:
 *
 *   - the frontend VDM preview ("Batch and Send"), which shows and sends it, and
 *   - the backend 849-resubmission path, which mints a NEW VDM server-side.
 *
 * SI2 is a fixed-width AS400 field, so a divergent copy of this logic would
 * silently truncate differently on one side. It lives here, shared, for that
 * reason — do not re-implement it locally.
 */

/**
 * AS400 SI2 field width. Mirrors `specialInstructions2: z.string().max(30)` in
 * the VDM contract — keep the two in sync.
 */
export const SI2_MAX_LENGTH = 30;

/**
 * Render batch numbers as `"Batch #1, 2, 3"`, dropping any that would overflow
 * the SI2 field. Truncation is deliberate and forward-only: the first batch
 * number always survives, and we stop at the first one that would not fit
 * rather than eliding from the middle.
 */
export function formatBatchSI2(batchNumbers: string[]): string {
  if (batchNumbers.length === 0) return "";
  const prefix = "Batch #";
  let result = prefix + batchNumbers[0];
  for (let i = 1; i < batchNumbers.length; i++) {
    const next = `${result}, ${batchNumbers[i]}`;
    if (next.length > SI2_MAX_LENGTH) break;
    result = next;
  }
  return result;
}

/**
 * The date whose month labels the claim on a VDM's SI1.
 *
 * A batch sent in the first three days of a month is almost always closing out
 * the PREVIOUS month, so it is labelled that way.
 */
export function vdmClaimPeriodDate(now: Date): Date {
  if (now.getDate() <= 3) {
    return new Date(now.getFullYear(), now.getMonth() - 1, 1);
  }
  return now;
}

/**
 * SI1 and SI3, which every VDM carries alongside the batch list in SI2.
 * SI1 labels the claim period; SI3 is a fixed contact line.
 */
export function defaultVdmSpecialInstructions(claimDate: Date): {
  si1: string;
  si3: string;
} {
  const month = claimDate.toLocaleString("en-US", { month: "long" });
  const year = claimDate.getFullYear();
  return {
    si1: `SD ${month} ${year}`,
    si3: "Contact: ShipandDebit@Rexel.ca",
  };
}

/**
 * The batch numbers for a single VDM, scoped to that VDM's own transactions. A
 * VDM is created per (division, tax province), so collecting the batch numbers
 * of its `transactionIds` (each transaction carries the batch number of its
 * DUNS location) yields only the batches related to THIS VDM — not every batch
 * in the division. Deduped and sorted ascending so the SI2 field shows a clean
 * consecutive run (batch numbers are pre-assigned in tax-province order).
 */
export function collectItemBatchNumbers(
  transactionIds: string[],
  batchNumbersByTransactionId: Record<string, number>,
): string[] {
  const seen = new Set<number>();
  for (const id of transactionIds) {
    const n = batchNumbersByTransactionId[id];
    if (n != null) seen.add(n);
  }
  return Array.from(seen)
    .sort((a, b) => a - b)
    .map(String);
}
